import { createIntl, createIntlCache, defineMessage, type MessageFormatElement } from 'react-intl';
import { TranslationKeys } from '@eventbrite/good-to-know';
import type { LocationProps } from '@/src/listings/types';
import { READ_MORE_MESSAGE, VIEW_ALL_MESSAGE } from '../_shared/constants';

type Messages = Record<string, string> | Record<string, MessageFormatElement[]>;

const intlCache = createIntlCache();

const GOOD_TO_KNOW_MESSAGE = defineMessage({
  id: 'Good to know',
  defaultMessage: 'Good to know',
  description: 'Good to know section title',
});

const REFUND_POLICY_MESSAGE = defineMessage({
  id: 'Refund Policy',
  defaultMessage: 'Refund Policy',
  description: 'Refund policy section title',
});

const HIGHLIGHTS_MESSAGE = defineMessage({
  id: 'Highlights',
  defaultMessage: 'Highlights',
  description: 'Highlights section title',
});

const TO_BE_ANNOUNCED_MESSAGE = defineMessage({
  id: 'To be announced',
  defaultMessage: 'To be announced',
  description: 'Location to be announced text',
});

const AGE_RESTRICTION_ALL_AGES_MESSAGE = defineMessage({
  id: 'All ages',
  defaultMessage: 'All ages',
  description: 'All ages label',
});

const AGE_RESTRICTION_MIN_TWENTY_ONE_MESSAGE = defineMessage({
  id: 'Ages 21+',
  defaultMessage: 'Ages 21+',
  description: 'Ages 21+ label',
});

const AGE_RESTRICTION_MIN_NINETEEN_MESSAGE = defineMessage({
  id: 'Ages 19+',
  defaultMessage: 'Ages 19+',
  description: 'Ages 19+ label',
});

const AGE_RESTRICTION_MIN_EIGHTEEN_MESSAGE = defineMessage({
  id: 'Ages 18+',
  defaultMessage: 'Ages 18+',
  description: 'Ages 18+ label',
});

const AGE_RESTRICTION_MIN_SEVENTEEN_MESSAGE = defineMessage({
  id: 'Ages 17+',
  defaultMessage: 'Ages 17+',
  description: 'Ages 17+ label',
});

const AGE_RESTRICTION_MIN_SIXTEEN_MESSAGE = defineMessage({
  id: 'Ages 16+',
  defaultMessage: 'Ages 16+',
  description: 'Ages 16+ label',
});

const AGE_RESTRICTION_MIN_FIFTEEN_MESSAGE = defineMessage({
  id: 'Ages 15+',
  defaultMessage: 'Ages 15+',
  description: 'Ages 15+ label',
});

const AGE_RESTRICTION_MIN_FOURTEEN_MESSAGE = defineMessage({
  id: 'Ages 14+',
  defaultMessage: 'Ages 14+',
  description: 'Ages 14+ label',
});

const AGE_RESTRICTION_MIN_THIRTEEN_MESSAGE = defineMessage({
  id: 'Ages 13+',
  defaultMessage: 'Ages 13+',
  description: 'Ages 13+ label',
});

const AGE_RESTRICTION_MIN_TWELVE_MESSAGE = defineMessage({
  id: 'Ages 12+',
  defaultMessage: 'Ages 12+',
  description: 'Ages 12+ label',
});

const AGE_RESTRICTION_UNDER_TWENTY_ONE_WITH_GUARDIAN_MESSAGE = defineMessage({
  id: 'Under 21 with parent or legal guardian',
  defaultMessage: 'Under 21 with parent or legal guardian',
  description: 'Under 21 with parent or legal guardian label',
});

const AGE_RESTRICTION_UNDER_EIGHTEEN_WITH_GUARDIAN_MESSAGE = defineMessage({
  id: 'Under 18 with parent or legal guardian',
  defaultMessage: 'Under 18 with parent or legal guardian',
  description: 'Under 18 with parent or legal guardian label',
});

const AGE_RESTRICTION_UNDER_SIXTEEN_WITH_GUARDIAN_MESSAGE = defineMessage({
  id: 'Under 16 with parent or legal guardian',
  defaultMessage: 'Under 16 with parent or legal guardian',
  description: 'Under 16 with parent or legal guardian label',
});

const AGE_RESTRICTION_UNDER_FOURTEEN_WITH_GUARDIAN_MESSAGE = defineMessage({
  id: 'Under 14 with parent or legal guardian',
  defaultMessage: 'Under 14 with parent or legal guardian',
  description: 'Under 14 with parent or legal guardian label',
});

const IN_PERSON_MESSAGE = defineMessage({
  id: 'In person',
  defaultMessage: 'In person',
  description: 'In person event label',
});

const ONLINE_MESSAGE = defineMessage({
  id: 'Online',
  defaultMessage: 'Online',
  description: 'Online event label',
});

const SCROLL_CAROUSEL_LEFT_MESSAGE = defineMessage({
  id: 'Scroll carousel left',
  defaultMessage: 'Scroll carousel left',
  description: 'Scroll carousel left aria label',
});

const SCROLL_CAROUSEL_RIGHT_MESSAGE = defineMessage({
  id: 'Scroll carousel right',
  defaultMessage: 'Scroll carousel right',
  description: 'Scroll carousel right aria label',
});

const FREE_PARKING_MESSAGE = defineMessage({
  id: 'Free parking',
  defaultMessage: 'Free parking',
  description: 'Free parking label',
});

const PAID_PARKING_MESSAGE = defineMessage({
  id: 'Paid parking',
  defaultMessage: 'Paid parking',
  description: 'Paid parking label',
});

const getIntl = (locale = 'en', messages: Messages = {}) => {
  return createIntl({ locale, messages, defaultLocale: 'en' }, intlCache);
};

// Extended type to include age restrictions that may not be in all versions
interface TranslationKeysWithAgeRestriction extends TranslationKeys {
  all_ages: string;
  ageMinTwentyOne: string;
  ageMinNineteen: string;
  ageMinEighteen: string;
  ageMinSeventeen: string;
  ageMinSixteen: string;
  ageMinFifteen: string;
  ageMinFourteen: string;
  ageMinThirteen: string;
  ageMinTwelve: string;
  ageUnderTwentyOneWithGuardian: string;
  ageUnderEighteenWithGuardian: string;
  ageUnderSixteenWithGuardian: string;
  ageUnderFourteenWithGuardian: string;
}

export const getTranslations = (
  location?: LocationProps,
  locale = 'en',
  messages: Messages = {},
): TranslationKeysWithAgeRestriction => {
  const intl = getIntl(locale, messages);
  const isTBDLocation = location && !location.isOnlineEvent && !location.hasAddress;

  return {
    goodToKnow: intl.formatMessage(GOOD_TO_KNOW_MESSAGE),
    refundPolicy: intl.formatMessage(REFUND_POLICY_MESSAGE),
    viewAll: intl.formatMessage(VIEW_ALL_MESSAGE),
    highlights: intl.formatMessage(HIGHLIGHTS_MESSAGE),
    all_ages: intl.formatMessage(AGE_RESTRICTION_ALL_AGES_MESSAGE),
    ageMinTwentyOne: intl.formatMessage(AGE_RESTRICTION_MIN_TWENTY_ONE_MESSAGE),
    ageMinNineteen: intl.formatMessage(AGE_RESTRICTION_MIN_NINETEEN_MESSAGE),
    ageMinEighteen: intl.formatMessage(AGE_RESTRICTION_MIN_EIGHTEEN_MESSAGE),
    ageMinSeventeen: intl.formatMessage(AGE_RESTRICTION_MIN_SEVENTEEN_MESSAGE),
    ageMinSixteen: intl.formatMessage(AGE_RESTRICTION_MIN_SIXTEEN_MESSAGE),
    ageMinFifteen: intl.formatMessage(AGE_RESTRICTION_MIN_FIFTEEN_MESSAGE),
    ageMinFourteen: intl.formatMessage(AGE_RESTRICTION_MIN_FOURTEEN_MESSAGE),
    ageMinThirteen: intl.formatMessage(AGE_RESTRICTION_MIN_THIRTEEN_MESSAGE),
    ageMinTwelve: intl.formatMessage(AGE_RESTRICTION_MIN_TWELVE_MESSAGE),
    ageUnderTwentyOneWithGuardian: intl.formatMessage(AGE_RESTRICTION_UNDER_TWENTY_ONE_WITH_GUARDIAN_MESSAGE),
    ageUnderEighteenWithGuardian: intl.formatMessage(AGE_RESTRICTION_UNDER_EIGHTEEN_WITH_GUARDIAN_MESSAGE),
    ageUnderSixteenWithGuardian: intl.formatMessage(AGE_RESTRICTION_UNDER_SIXTEEN_WITH_GUARDIAN_MESSAGE),
    ageUnderFourteenWithGuardian: intl.formatMessage(AGE_RESTRICTION_UNDER_FOURTEEN_WITH_GUARDIAN_MESSAGE),
    inPerson: isTBDLocation ? intl.formatMessage(TO_BE_ANNOUNCED_MESSAGE) : intl.formatMessage(IN_PERSON_MESSAGE),
    online: intl.formatMessage(ONLINE_MESSAGE),
    scrollLeft: intl.formatMessage(SCROLL_CAROUSEL_LEFT_MESSAGE),
    scrollRight: intl.formatMessage(SCROLL_CAROUSEL_RIGHT_MESSAGE),
    freeParking: intl.formatMessage(FREE_PARKING_MESSAGE),
    paidParking: intl.formatMessage(PAID_PARKING_MESSAGE),
    readMore: intl.formatMessage(READ_MORE_MESSAGE),
  };
};

export const ageRestrictionsMapTranslations = {
  all_ages: AGE_RESTRICTION_ALL_AGES_MESSAGE,
  '21+': AGE_RESTRICTION_MIN_TWENTY_ONE_MESSAGE,
  '19+': AGE_RESTRICTION_MIN_NINETEEN_MESSAGE,
  '18+': AGE_RESTRICTION_MIN_EIGHTEEN_MESSAGE,
  '17+': AGE_RESTRICTION_MIN_SEVENTEEN_MESSAGE,
  '16+': AGE_RESTRICTION_MIN_SIXTEEN_MESSAGE,
  '15+': AGE_RESTRICTION_MIN_FIFTEEN_MESSAGE,
  '14+': AGE_RESTRICTION_MIN_FOURTEEN_MESSAGE,
  '13+': AGE_RESTRICTION_MIN_THIRTEEN_MESSAGE,
  '12+': AGE_RESTRICTION_MIN_TWELVE_MESSAGE,
  under_21_with_guardian: AGE_RESTRICTION_UNDER_TWENTY_ONE_WITH_GUARDIAN_MESSAGE,
  under_18_with_guardian: AGE_RESTRICTION_UNDER_EIGHTEEN_WITH_GUARDIAN_MESSAGE,
  under_16_with_guardian: AGE_RESTRICTION_UNDER_SIXTEEN_WITH_GUARDIAN_MESSAGE,
  under_14_with_guardian: AGE_RESTRICTION_UNDER_FOURTEEN_WITH_GUARDIAN_MESSAGE,
};
